import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, PartyPopper, Share2, Copy, Check } from 'lucide-react';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'wm_milestone_seen_v1';

function launchConfetti() {
  // Burst pattern that reads "gold celebration" without being obnoxious.
  const duration = 4_000;
  const end = Date.now() + duration;
  const colors = ['#d4af37', '#f4d576', '#ffffff', '#1a1f2e'];
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors,
      scalar: 1.1,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors,
      scalar: 1.1,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

/**
 * Auto-launches a fullscreen celebration when the millionaire pot reaches its goal.
 * Also supports an explicit `forceOpen` prop so an admin can preview the experience.
 *
 * Polls /api/videos/milestone/status every 30s; opens on (milestone_reached && exists).
 * Dismissal remembered per video_id so uploading a new milestone re-triggers.
 */
export const MilestoneCelebration = ({ forceOpen = false, onClose: externalOnClose }) => {
  const [open, setOpen] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState('');
  const videoRef = useRef(null);

  // Poll live status (skip when admin is force-previewing)
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setVideoId('preview');
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/videos/milestone/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data.milestone_reached && data.exists && data.video_id) {
          const seen = localStorage.getItem(STORAGE_KEY);
          if (seen !== data.video_id) {
            setVideoId(data.video_id);
            setOpen(true);
          }
        }
      } catch {
        /* silent */
      }
    };
    check();
    const id = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [forceOpen]);

  // Fire confetti whenever the modal opens
  useEffect(() => {
    if (open) launchConfetti();
  }, [open]);

  const handleClose = useCallback(() => {
    if (videoId && !forceOpen) localStorage.setItem(STORAGE_KEY, videoId);
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { /* noop */ }
    }
    setOpen(false);
    if (externalOnClose) externalOnClose();
  }, [videoId, forceOpen, externalOnClose]);

  const shareText = "WeMasterminds just hit $1M! Join the community building wealth together:";
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'WeMasterminds hit $1M!',
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or browser blocked — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setFallbackMessage('Copy this: ' + shareUrl);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      data-testid="milestone-celebration-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-4xl">
        <button
          type="button"
          onClick={handleClose}
          className="absolute -top-12 right-0 text-white/80 hover:text-white flex items-center gap-2 text-sm uppercase tracking-widest"
          data-testid="milestone-close-btn"
        >
          Close <X className="w-5 h-5" />
        </button>

        <div className="rounded-sm overflow-hidden border border-gold/40 shadow-[0_0_60px_rgba(212,175,55,0.35)] bg-black">
          <div className="flex items-center gap-3 px-6 py-4 bg-gold-gradient text-black font-playfair">
            <PartyPopper className="w-6 h-6" />
            <div className="flex-1">
              <p className="text-lg font-bold uppercase tracking-wider">$1 Million Milestone Reached</p>
              <p className="text-sm">Celebrating our community's first $1M milestone</p>
            </div>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-black/80 hover:bg-black text-gold text-xs uppercase tracking-widest font-bold transition-colors"
              data-testid="milestone-share-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied' : 'Share'}
            </button>
          </div>

          <video
            ref={videoRef}
            src={`${API_URL}/api/videos/milestone/stream`}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="w-full aspect-video bg-black"
            data-testid="milestone-video-player"
          />

          {fallbackMessage && (
            <p className="px-6 py-2 text-xs text-gold bg-black/40 text-center">{fallbackMessage}</p>
          )}
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 text-white/60 text-xs uppercase tracking-widest">
          <Copy className="w-3 h-3" />
          <span>Click "Share" to invite others to celebrate</span>
        </div>
      </div>
    </div>
  );
};

export default MilestoneCelebration;
